import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../config';
import SEO from '../components/SEO';
import PublicNavHeader from '../components/PublicNavHeader';
import Footer from '../components/Footer';

import {
  ArrowRight,
  Briefcase,
  Buildings,
  CheckCircle,
  CircleNotch,
  Envelope,
  Eye,
  FileText,
  Globe,
  MapPin,
  Shield,
  Sparkle,
  UsersThree
} from '@phosphor-icons/react';
const PartnerApply = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [options, setOptions] = useState({
    service_locations: [],
    industries: [],
    services: []
  });
  
  const [formData, setFormData] = useState({
    business_name: '',
    website_or_linkedin: '',
    country: '',
    city: '',
    service_locations: [],
    industries_served: [],
    services_offered: [],
    about_business: '',
    contact_email: '',
    contact_phone: '',
    user_id: ''  // Man & Machine User ID for Partner Pro verification
  });

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/partners/options/all`);
      if (response.ok) {
        const data = await response.json();
        setOptions(data);
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    }
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData.service_locations.length === 0) {
      setError('Please select at least one service location');
      setLoading(false);
      return;
    }
    if (formData.industries_served.length === 0) {
      setError('Please select at least one industry');
      setLoading(false);
      return;
    }
    if (formData.services_offered.length === 0) {
      setError('Please select at least one service');
      setLoading(false);
      return;
    }
    if (formData.about_business.split(/\s+/).length < 20) {
      setError('Please provide more details about your business (at least 20 words)');
      setLoading(false);
      return;
    }

    try {
      const headers = { 'Content-Type': 'application/json' };
      
      // Add auth if logged in
      const sessionToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('session_token='))
        ?.split('=')[1];
      
      if (sessionToken) {
        headers['Cookie'] = `session_token=${sessionToken}`;
      }

      const response = await fetch(`${BACKEND_URL}/api/partners/apply`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(data.detail || 'Failed to submit application');
      }
    } catch (err) {
      setError('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success State
  if (submitted) {
    return (
      <>
        <SEO title="Application Submitted - Man & Machine Partners" />
        <PublicNavHeader />
        <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg w-full text-center"
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#14F195] to-[#10B981] flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">
              Thanks for Applying!
            </h1>
            <p className="text-[#A0AEC0] text-lg mb-8">
              Our team will review your application shortly. We&apos;ll be in touch via email once your application has been reviewed.
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold rounded-full hover:opacity-90 transition-opacity"
            >
              Return to Home
              <ArrowRight size={18} />
            </a>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title="Apply to Become a Partner - Man & Machine" 
        description="Join the Man & Machine Partner Program. Build, deploy & scale AI solutions professionally as an AI consultant, agency, or automation professional."
      />
      <PublicNavHeader />
      
      <div className="min-h-screen bg-[#0a0a0f]">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#6366F1]/10 via-transparent to-[#EC4899]/10" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#6366F1]/20 blur-[120px] rounded-full" />
          
          <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#6366F1] text-sm font-medium mb-6">
                <UsersThree size={16} />
                Partner Program
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Apply to Become a{' '}
                <span className="bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-transparent bg-clip-text">
                  Man & Machine Partner
                </span>
              </h1>
              
              <p className="text-xl text-[#A0AEC0] mb-8">
                Build, Deploy & Scale AI Solutions — Professionally
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Info */}
            <div className="lg:col-span-1 space-y-8">
              {/* Intro */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="glass rounded-2xl p-6"
              >
                <h2 className="text-lg font-semibold text-white mb-3">About the Partner Program</h2>
                <p className="text-[#A0AEC0] text-sm leading-relaxed">
                  Man & Machine Partners are AI consultants, agencies, and operators who help businesses design, deploy, and manage real-world AI agents using the Man & Machine platform.
                </p>
                <p className="text-[#A0AEC0] text-sm leading-relaxed mt-3">
                  If you work with clients and want a structured, scalable way to deliver AI, we&apos;d love to hear from you.
                </p>
              </motion.div>

              {/* Who This Is For */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="glass rounded-2xl p-6"
              >
                <h2 className="text-lg font-semibold text-white mb-4">Who This Is For</h2>
                <ul className="space-y-3">
                  {[
                    'AI consultants & strategists',
                    'Automation agencies',
                    'No-code / low-code builders',
                    'Digital agencies delivering AI solutions',
                    'Professionals moving into AI services'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#A0AEC0] text-sm">
                      <CheckCircle size={16} className="text-[#14F195] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Why Become a Partner */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="glass rounded-2xl p-6"
              >
                <h2 className="text-lg font-semibold text-white mb-4">Why Become a Partner</h2>
                <ul className="space-y-3">
                  {[
                    { icon: Briefcase, text: 'Professional AI delivery platform' },
                    { icon: Sparkle, text: 'Repeatable, scalable client work' },
                    { icon: Eye, text: 'Visibility via the Find a Partner directory' },
                    { icon: Shield, text: 'Partner-only access via Partner Pro' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#A0AEC0] text-sm">
                      <item.icon size={16} className="text-[#6366F1] flex-shrink-0" />
                      {item.text}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* How It Works */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="glass rounded-2xl p-6"
              >
                <h2 className="text-lg font-semibold text-white mb-4">How It Works</h2>
                <ol className="space-y-4">
                  {[
                    'Apply using the form',
                    'Application is reviewed by Man & Machine',
                    'Approved partners are published in the directory'
                  ].map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-[#A0AEC0] text-sm pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
                <p className="text-xs text-gray-500 mt-4 italic">
                  Partner listings are curated to maintain quality and trust.
                </p>
              </motion.div>
            </div>

            {/* Right Column - Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Partner Application</h2>
                
                {/* Partner Pro Requirement Notice */}
                <div className="mb-6 p-4 bg-[#6366F1]/10 border border-[#6366F1]/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Shield size={20} className="text-[#6366F1] flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-white mb-1">Partner Pro Subscription Required</h3>
                      <p className="text-xs text-[#A0AEC0] leading-relaxed">
                        To be listed in the Partner Directory, you must have an active <span className="text-[#6366F1] font-medium">Partner Pro</span> subscription. 
                        Your User ID can be found in your <a href="/account" className="text-[#6366F1] hover:underline">Account Settings</a> page.
                      </p>
                    </div>
                  </div>
                </div>
                
                {error && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Business Name */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Business Name *
                    </label>
                    <div className="relative">
                      <Buildings size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type="text"
                        required
                        value={formData.business_name}
                        onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:border-[#6366F1] focus:outline-none"
                        placeholder="Your company or agency name"
                      />
                    </div>
                  </div>

                  {/* Website or LinkedIn */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Website or LinkedIn Profile *
                    </label>
                    <div className="relative">
                      <Globe size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type="url"
                        required
                        value={formData.website_or_linkedin}
                        onChange={(e) => setFormData({ ...formData, website_or_linkedin: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:border-[#6366F1] focus:outline-none"
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  {/* Location */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Country *
                      </label>
                      <div className="relative">
                        <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                        <input
                          type="text"
                          required
                          value={formData.country}
                          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                          className="w-full pl-12 pr-4 py-3 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:border-[#6366F1] focus:outline-none"
                          placeholder="Country"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:border-[#6366F1] focus:outline-none"
                        placeholder="City"
                      />
                    </div>
                  </div>

                  {/* Service Locations */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Service Locations * <span className="text-gray-500 font-normal">(Select all that apply)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {options.service_locations.map((loc) => (
                        <button
                          key={loc}
                          type="button"
                          onClick={() => handleMultiSelect('service_locations', loc)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                            formData.service_locations.includes(loc)
                              ? 'bg-[#6366F1] text-white'
                              : 'bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
                          }`}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Industries Served */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Industries Served * <span className="text-gray-500 font-normal">(Select all that apply)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {options.industries.map((ind) => (
                        <button
                          key={ind}
                          type="button"
                          onClick={() => handleMultiSelect('industries_served', ind)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                            formData.industries_served.includes(ind)
                              ? 'bg-[#6366F1] text-white'
                              : 'bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
                          }`}
                        >
                          {ind}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Services Offered */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Services Offered * <span className="text-gray-500 font-normal">(Select all that apply)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {options.services.map((svc) => (
                        <button
                          key={svc}
                          type="button"
                          onClick={() => handleMultiSelect('services_offered', svc)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                            formData.services_offered.includes(svc)
                              ? 'bg-[#6366F1] text-white'
                              : 'bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
                          }`}
                        >
                          {svc}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* About Business */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      About Your Business * <span className="text-gray-500 font-normal">(Max 300 words)</span>
                    </label>
                    <div className="relative">
                      <FileText size={18} className="absolute left-4 top-4 text-gray-500" />
                      <textarea
                        required
                        rows={5}
                        value={formData.about_business}
                        onChange={(e) => setFormData({ ...formData, about_business: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:border-[#6366F1] focus:outline-none resize-none"
                        placeholder="Tell us about your business, experience, and why you want to become a partner..."
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.about_business.split(/\s+/).filter(Boolean).length} / 300 words
                    </p>
                  </div>

                  {/* Contact Email */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Contact Email *
                    </label>
                    <div className="relative">
                      <Envelope size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type="email"
                        required
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:border-[#6366F1] focus:outline-none"
                        placeholder="you@company.com"
                      />
                    </div>
                  </div>

                  {/* Contact Phone */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Contact Phone <span className="text-gray-500 font-normal">(Optional)</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:border-[#6366F1] focus:outline-none"
                      placeholder="+1 234 567 8900"
                    />
                  </div>

                  {/* User ID */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Man & Machine User ID * <span className="text-gray-500 font-normal">(Found in Account Settings)</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.user_id}
                      onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:border-[#6366F1] focus:outline-none font-mono text-sm"
                      placeholder="e.g., user_abc123def456"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This helps us verify your Partner Pro subscription status.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold text-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <CircleNotch size={20} className="animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <ArrowRight size={20} />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    By submitting, you agree to our review process. Partner listings are curated to maintain quality and trust.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
      
      <Footer />
    </>
  );
};

export default PartnerApply;
